import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createApi } from 'unsplash-js';
import authTokenUtil from '../account/authTokenUtil';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import notFound from '../images/empty/not-found.svg';
import Link from '../utils/router/Link';
import type { DisplayMode } from './BannerImageList';
import BannerImageList, {
  BannerImageListButton,
  BannerImageSkeleton,
} from './BannerImageList';
import Form, { Field } from './Form';
import InfiniteScrollWaypoint from './InfiniteScrollWaypoint';
import LoadingIndicator from './LoadingIndicator';
import SVGIcon from './SVGIcon';
import UnsplashColorPicker from './UnsplashColorPicker';
import type { SupportedColorId } from './UnsplashColors';
import * as styles from './UnsplashPicker.module.scss';

type Photos = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof createApi>['search']['getPhotos']>
  >['response']
>;

const getUnsplashApi = _.memoize(() =>
  createApi({
    apiUrl: `https://${window.__slab.PUBLIC_ORG.effectiveHost}/unsplash`,
    headers: {
      Authorization: authTokenUtil.get() ?? '',
    },
  }),
);

const DEFAULT_PHOTOS: Photos = {
  total: 0,
  total_pages: 0,
  results: [],
};

const TOPIC_SLUGS = [
  'wallpapers',
  'travel',
  'nature',
  'textures-patterns',
  '3d-renders',
] as const;

function useUnsplashPhotoSearch({
  query,
  colorId,
  perPage,
}: {
  query: string;
  colorId?: SupportedColorId;
  perPage: number;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [photos, setPhotos] = useState<Photos>(DEFAULT_PHOTOS);
  const currentPage = Math.ceil(photos.results.length / perPage);
  const hasMore = photos.total_pages > currentPage;

  const topicSlug = useMemo(() => {
    const slug = _.sample(TOPIC_SLUGS);
    if (slug == null) throw new Error("Topic slug can't be null");
    return slug;
  }, []);

  const loadPage = useCallback(
    async (page: number, color: SupportedColorId | undefined) => {
      setIsLoading(true);
      try {
        if (query !== '') {
          return await getUnsplashApi().search.getPhotos({
            query,
            orientation: 'landscape',
            color,
            perPage,
            page,
          });
        }

        const results = await getUnsplashApi().topics.getPhotos({
          topicIdOrSlug: topicSlug,
          perPage,
          page,
          orientation: 'landscape',
        });

        if (results.response) {
          return {
            response: {
              results: results.response.results,
              total: results.response.total,
              total_pages: results.response.total / perPage,
            },
          };
        }

        return { response: null };
      } finally {
        setIsLoading(false);
      }
    },
    [query, perPage, topicSlug],
  );

  const fetchMore = useCallback(async () => {
    if (hasMore) {
      await loadPage(currentPage + 1, colorId).then((result) => {
        if (result.response) {
          setPhotos((current) => ({
            ...current,
            results: [...current.results, ...result.response.results],
          }));
        }
      });
    }
  }, [loadPage, hasMore, currentPage, colorId]);

  useEffect(() => {
    setPhotos(DEFAULT_PHOTOS);
    loadPage(1, colorId).then((result) => {
      if (result.response) {
        setPhotos(result.response);
      }
    });
  }, [loadPage, colorId]);

  return {
    results: photos.results,
    fetchMore: hasMore ? fetchMore : null,
    isLoading,
  };
}

function scaledUrl(rawUrl: string, width: number) {
  const url = new URL(rawUrl);
  url.search = new URLSearchParams({
    w: `${width}`,
  }).toString();
  return url.toString();
}

const UnsplashPickerContext = React.createContext<{
  query: string;
  setQuery: (query: string) => void;
  colorId: SupportedColorId | null;
  setColorId: (colorId: SupportedColorId | null) => void;
  displayMode: DisplayMode;
  onPick: (banner: { image: string; unsplashId: string }) => void;
}>({
  query: '',
  setQuery: () => {},
  colorId: null,
  setColorId: () => {},
  displayMode: 'grid',
  onPick: () => {},
});

const UnsplashPickerSearchForm = () => {
  const { query, setQuery, colorId, setColorId } = useContext(
    UnsplashPickerContext,
  );

  return (
    <div className={styles.searchFormContainer}>
      <Form<{ query: string }>
        initialValues={{ query }}
        onSubmit={(values) => setQuery(values.query)}
      >
        <Field
          autoFocus
          name="query"
          placeholder="Search for an image"
          type="text"
          margin="none"
          prepend={
            <SVGIcon className={styles.searchInputPrepend} size={16}>
              {searchIcon}
            </SVGIcon>
          }
        />
      </Form>
      <UnsplashColorPicker onChange={setColorId} value={colorId} />
    </div>
  );
};

const UnsplashPickerResults = () => {
  const { query, colorId, displayMode, onPick } = useContext(
    UnsplashPickerContext,
  );
  const [fetchingImageId, setFetchingImageId] = useState<string | null>(null);
  const imagesPerPage = displayMode === 'grid' ? 15 : 10;
  const { results, fetchMore, isLoading } = useUnsplashPhotoSearch({
    query,
    colorId: query === '' ? undefined : (colorId ?? undefined),
    perPage: imagesPerPage,
  });

  const isShowingEmptyState = results.length === 0 && !isLoading;

  return (
    <>
      {isShowingEmptyState ? (
        <div className={styles.emptyStateContainer}>
          <img src={notFound} alt="" />
          <div>No results found</div>
        </div>
      ) : (
        <div className={styles.resultsContainer}>
          {results.length === 0 && isLoading ? (
            <BannerImageList displayMode={displayMode}>
              {_.range(imagesPerPage).map((i) => (
                <BannerImageSkeleton key={i} />
              ))}
            </BannerImageList>
          ) : (
            <>
              <BannerImageList displayMode={displayMode}>
                {results.map(
                  ({
                    id,
                    urls,
                    alt_description: alt,
                    links: { download_location: downloadLocation },
                    user: { name, links },
                  }) => {
                    const isLoadingImage = id === fetchingImageId;
                    const profileLink = new URL(links.html);
                    profileLink.search = new URLSearchParams({
                      utm_source: 'slab',
                      utm_medium: 'referral',
                    }).toString();

                    return (
                      <div
                        key={id}
                        className={classNames(styles.imageContainer, {
                          [styles.isLoading]: isLoadingImage,
                        })}
                      >
                        <BannerImageListButton
                          onClick={async () => {
                            setFetchingImageId(id);

                            const url = scaledUrl(urls.raw, 2880);
                            await fetch(url);
                            await getUnsplashApi().photos.trackDownload({
                              downloadLocation,
                            });
                            setFetchingImageId(null);

                            onPick({
                              image: url,
                              unsplashId: id,
                            });
                          }}
                          src={urls.regular}
                          alt={alt ?? 'Unsplash Image'}
                        />
                        <div className={styles.imageOverlay}>
                          {isLoadingImage ? (
                            <LoadingIndicator delay={0} />
                          ) : (
                            <div className={styles.authorContainer}>
                              <Link to={profileLink.toString()} newTab>
                                {name}
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  },
                )}
              </BannerImageList>
              <InfiniteScrollWaypoint fetchMore={fetchMore} />
            </>
          )}
        </div>
      )}
    </>
  );
};

interface UnsplashPickerRootProps {
  displayMode: DisplayMode;
  onPick(banner: { image: string; unsplashId: string }): void;
  children: React.ReactNode;
}

const UnsplashPickerRoot = ({
  displayMode,
  onPick,
  children,
}: UnsplashPickerRootProps) => {
  const [query, setQuery] = useState('');
  const [colorId, setColorId] = useState<SupportedColorId | null>(null);

  const contextValue = useMemo(
    () => ({ query, setQuery, colorId, setColorId, displayMode, onPick }),
    [query, setQuery, colorId, setColorId, displayMode, onPick],
  );

  return (
    <div className={styles.container}>
      <UnsplashPickerContext.Provider value={contextValue}>
        {children}
      </UnsplashPickerContext.Provider>
    </div>
  );
};

export default {
  Root: UnsplashPickerRoot,
  SearchForm: UnsplashPickerSearchForm,
  Results: UnsplashPickerResults,
};
